import { ChevronDownIcon, ChevronUpIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { useIsSectionStalled } from "@utils/sectionStall";
import { FC, Fragment, useState } from "react";

import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { FieldGroup, FieldSet } from "@fronterahealth/frontera-ui-components/ui/field";

import { ApiReportSectionsAiGeneratedStatusChoices, ReportSectionsType } from "@api/graphql/types-and-hooks";
import { ReportFieldAddContent } from "@components/ReportField/ReportFieldAddContent";
import { ReportFieldRenderer } from "@components/ReportField/ReportFieldRenderer";
import { ReportFieldSkeleton } from "@components/ReportField/ReportFieldSkeleton";
import { ReportSectionActionMenu } from "@components/ReportField/ReportSection/ReportSectionActionMenu";
import { ReportSectionAddContent } from "@components/ReportField/ReportSection/ReportSectionAddContent";
import { ReportSectionRecommendationsPanel } from "@components/ReportField/ReportSection/ReportSectionRecommendationsPanel";
import { ReportSectionRecovery } from "@components/ReportField/ReportSection/ReportSectionRecovery";
import { ReportSectionTitle } from "@components/ReportField/ReportSection/ReportSectionTitle";
import { useCanAddContent, useCanAddSection } from "@components/ReportField/capabilities";
import { getSectionRenderState } from "@components/SectionWidget/sectionStateClasses";
import { getSortedFieldsByRankOrder } from "@pages/EvaluationDetails/Components/helper";

export interface ReportSectionProps {
  section: ReportSectionsType;
  // Clinical's "Expand All"/"Contract All" toolbar resets every section's collapsed
  // state by passing a new object each click — plain `{ value: boolean }` wouldn't
  // re-trigger the effect below if the user had since toggled a section back open.
  isCollapsed?: { value: boolean };
  variant?: "ghost";
  disabled?: boolean;
}

// Section chrome plus its field content: title, action menu, add-section/add-content
// panels, and the loading/error states, around every field rendered through
// ReportFieldRenderer and the section's recommendations panel.
export const ReportSection: FC<ReportSectionProps> = ({ section, isCollapsed, variant, disabled }) => {
  const canAddSection = useCanAddSection(section);
  const canAddContent = useCanAddContent(section);
  const [isSectionCollapsed, setIsSectionCollapsed] = useState(isCollapsed?.value ?? false);

  // Adjust state during render rather than in an effect: `isCollapsed` is a
  // fresh object on every "Expand All"/"Contract All" click, and we want that
  // to win over whatever a user had toggled locally in between clicks.
  const [prevIsCollapsed, setPrevIsCollapsed] = useState(isCollapsed);
  if (isCollapsed !== prevIsCollapsed) {
    setPrevIsCollapsed(isCollapsed);
    setIsSectionCollapsed(isCollapsed?.value ?? false);
  }

  const sectionStillGenerating =
    section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending;
  const sectionErrored = section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.Failure;
  // Still SECTION_PREDICTION_PENDING server-side, but generating past the threshold. (PROA-2110)
  const sectionStalled = useIsSectionStalled(section);
  // Progress presentation (indicator, skeleton, dimming) stops once stalled, and the recovery
  // panel takes over. The action menu and title editing stay keyed off
  // `sectionStillGenerating`: renaming, reordering or moving a stalled section bumps
  // `lastUpdatedDate`, which would reset the stall clock and hide Regenerate again.
  const { showLoading, showRecovery } = getSectionRenderState({
    isLoading: sectionStillGenerating,
    isError: sectionErrored,
    isStalled: sectionStalled,
  });

  const fields = getSortedFieldsByRankOrder(section);

  return (
    <>
      <FieldSet
        data-testid={`report-section-${section.title}`}
        className={cn(
          "outline-solid outline-1 outline-border-medium px-8 py-6 pageBreak rounded-3xl bg-white",
          !canAddSection && "mb-4",
          "scroll-mt-48",
        )}
        id={`heading-${section.title.replace(/\s+/g, "-")}`}
        data-sticky-nav-header
      >
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            {isCollapsed && (
              <button
                type="button"
                aria-label={isSectionCollapsed ? "Expand section" : "Collapse section"}
                className="h-4 w-4 text-text-secondary"
                onClick={() => setIsSectionCollapsed(!isSectionCollapsed)}
              >
                {isSectionCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
              </button>
            )}
            <div className="border p-2 rounded-sm">
              <DocumentTextIcon className="size-6" />
            </div>
            <ReportSectionTitle section={section} isEditLocked={sectionStillGenerating} showLoading={showLoading} />
          </div>
          {!sectionStillGenerating && <ReportSectionActionMenu section={section} />}
        </div>
        {showRecovery ? (
          <ReportSectionRecovery section={section} isStalled={sectionStalled} />
        ) : (
          <div hidden={isSectionCollapsed} className={showLoading ? "pointer-events-none opacity-20" : undefined}>
            <FieldGroup>
              {fields.map((field) => (
                <Fragment key={field.id}>
                  <ReportFieldRenderer section={section} field={field} variant={variant} disabled={disabled} />
                  {canAddContent && <ReportFieldAddContent field={field} section={section} />}
                </Fragment>
              ))}
              {/* A section with no fields yet (freshly added, or template-seeded empty) has no
                  field to anchor an "Add Content" control to — render a leading one so it can
                  actually be filled in. */}
              {canAddContent && !fields.length && <ReportFieldAddContent section={section} />}
              {showLoading && !fields.length && (
                <>
                  <ReportFieldSkeleton classNameField="h-24" classNameTitle="w-32" />
                  <ReportFieldSkeleton classNameField="h-32" classNameTitle="w-24" />
                  <ReportFieldSkeleton classNameField="h-20" classNameTitle="w-40" />
                </>
              )}
            </FieldGroup>
            <ReportSectionRecommendationsPanel section={section} />
          </div>
        )}
      </FieldSet>
      {canAddSection && (
        <ReportSectionAddContent pageLocation={section.pageLocation} newRankOrder={section.rankOrder + 1} />
      )}
    </>
  );
};
